import React, { useState, useEffect } from "react";
import "../css/CollegeBill.css";
import {
  Search,
  User,
  X,
  Copy,
  CheckCircle,
  CreditCard,
  Smartphone,
  Building,
  Send,
} from "lucide-react";
import StudentBillPDF from "../components/StudentBillPDF";

function CollegeBill() {
  // State variables
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [billDetails, setBillDetails] = useState({
    title: "College Course Fee", // Default value
    amount: "",
    remarks: "",
  });
  const [copied, setCopied] = useState(false);
  const [showBillPreview, setShowBillPreview] = useState(false);
  // Add new state for payment methods
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodDetails, setSelectedPaymentMethodDetails] =
    useState(null);

  // Fetch students data from API
  useEffect(() => {
    fetchStudents();
    fetchPaymentMethods(); // Add this new function call
  }, []);

  const fetchStudents = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        "https://payroll-api.uniums.com/api/students/students"
      );

      if (!response.ok) {
        throw new Error("Failed to fetch students");
      }

      const data = await response.json();
      setStudents(data);
      setError(null);
    } catch (err) {
      setError("Error fetching students: " + err.message);
      console.error("Error fetching students:", err);
    } finally {
      setIsLoading(false);
    }
  };


  // New function to fetch payment methods
  const fetchPaymentMethods = async () => {
    try {
      const response = await fetch(
        "https://payroll-api.uniums.com/api/payment-methods"
      );

      if (!response.ok) {
        throw new Error("Failed to fetch payment methods");
      }

      const data = await response.json();
      // Check if methods exist and set them
      if (data && data.methods && Array.isArray(data.methods)) {
        // Add cash option if it doesn't exist
        const cashOption = {
          type: "cash",
          displayName: "Cash",
          accountName: "Cash Payment",
        };

        // Check if cash option already exists
        const hasCashOption = data.methods.some(
          (method) => method.type === "cash"
        );

        if (!hasCashOption) {
          // Add cash option to the beginning of the array
          setPaymentMethods([cashOption, ...data.methods]);
        } else {
          setPaymentMethods(data.methods);
        }
      } else {
        // If no methods from API, at least have cash option
        setPaymentMethods([
          {
            type: "cash",
            displayName: "Cash",
            accountName: "Cash Payment",
          },
        ]);
      }
    } catch (err) {
      console.error("Error fetching payment methods:", err);
      // If API fails, set at least cash option
      setPaymentMethods([
        {
          type: "cash",
          displayName: "Cash",
          accountName: "Cash Payment",
        },
      ]);
    }
  };

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === "" || selectedStudent?.name === searchQuery) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = students.filter(
      (student) =>
        student.name.toLowerCase().includes(query) ||
        student.rollNo.toLowerCase().includes(query) ||
        (student.regNo && student.regNo.toLowerCase().includes(query))
    );

    setSearchResults(results);
    setShowDropdown(true);
  }, [searchQuery, students, selectedStudent]);

  // Handle student selection
  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setSearchQuery(student.name);
    setShowDropdown(false);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillDetails({
      ...billDetails,
      [name]: value,
    });
  };

  // Handle payment method change - update to set the selected payment method details
  const handlePaymentMethodChange = (e) => {
    const selectedValue = e.target.value;
    setPaymentMethod(selectedValue);

    // Find the selected payment method details
    if (selectedValue) {
      const methodDetails = paymentMethods.find(
        (method) => method.type === selectedValue
      );
      setSelectedPaymentMethodDetails(methodDetails);
    } else {
      setSelectedPaymentMethodDetails(null);
    }
  };

  // Handle copy UPI ID - updated to use the selected payment method's UPI ID
  const handleCopyUpiId = () => {
    if (selectedPaymentMethodDetails && selectedPaymentMethodDetails.upiId) {
      navigator.clipboard.writeText(selectedPaymentMethodDetails.upiId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    // Instead of alert, show bill preview
    setShowBillPreview(true);
  };

  // Handle closing bill preview
  const handleCloseBillPreview = () => {
    setShowBillPreview(false);
    // Reset form after closing the preview
    setSelectedStudent(null);
    setSearchQuery("");
    setBillDetails({ title: "College Course Fee", amount: "", remarks: "" });
    setPaymentMethod("");
    setSelectedPaymentMethodDetails(null);
  };

  // Get Avatar color based on name
  const getAvatarColor = (name) => {
    const colors = [
      "#3b82f6",
      "#4f46e5",
      "#8b5cf6",
      "#d946ef",
      "#f43f5e",
      "#f97316",
      "#eab308",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % colors.length);
    return colors[index];
  };

  // Get QR code URL
  const getQrCodeUrl = (filename) => {
    if (!filename) return null;
    return `https://payroll-api.uniums.com/uploads/qrcodes/${filename}`;
  };

  return (
    <div className="college-bill-container">
      {/* Error message if API fails */}
      {error && <div className="error-message">{error}</div>}

      {/* Header */}
      <div className="college-bill-header">
        <h1>Create New College Bill</h1>
        <p>Search for a student and create a new bill payment</p>
      </div>

      {/* Search Student Section */}
      <div className="search-student-section">
        <h2>Search Student</h2>
        <div className="search-input-container">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, registration number, or roll number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery && setShowDropdown(true)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery("")}>
              <X size={16} />
            </button>
          )}
          {isLoading && (
            <div className="search-loading">
              <div className="spinner-small"></div>
            </div>
          )}
          {showDropdown && searchResults.length > 0 && (
            <div className="search-dropdown">
              {searchResults.map((student) => (
                <div
                  key={student._id || student.id || student.rollNo}
                  className="search-dropdown-item"
                  onClick={() => handleSelectStudent(student)}
                >
                  <div className="student-info">
                    <div
                      className="student-avatar"
                      style={{ backgroundColor: getAvatarColor(student.name) }}
                    >
                      {student.name.charAt(0)}
                    </div>
                    <div className="student-info-text">
                      <h4>{student.name}</h4>
                      <p>
                        {student.rollNo} • {student.department}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {showDropdown && searchResults.length === 0 && (
            <div className="search-dropdown">
              <div className="search-dropdown-item">
                No students found. Try a different search term.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Selected Student Details */}
      {selectedStudent && (
        <div className="student-details-section">
          <div className="student-details-header">
            <div
              className="student-details-avatar"
              style={{ backgroundColor: getAvatarColor(selectedStudent.name) }}
            >
              {selectedStudent.name.charAt(0)}
            </div>
            <div className="student-details-name">
              <h3>{selectedStudent.name}</h3>
              <p>
                {selectedStudent.rollNo} • {selectedStudent.department}
              </p>
            </div>
          </div>

          <div className="student-details-row">
            <div className="student-details-col">
              <div className="student-detail-item">
                <span className="student-detail-label">Roll Number</span>
                <span className="student-detail-value">
                  {selectedStudent.rollNo}
                </span>
              </div>
              <div className="student-detail-item">
                <span className="student-detail-label">Class</span>
                <span className="student-detail-value">
                  {selectedStudent.class}
                </span>
              </div>
            </div>
            <div className="student-details-col">
              <div className="student-detail-item">
                <span className="student-detail-label">Email</span>
                <span className="student-detail-value">
                  {selectedStudent.email}
                </span>
              </div>
              <div className="student-detail-item">
                <span className="student-detail-label">Phone</span>
                <span className="student-detail-value">
                  {selectedStudent.phone}
                </span>
              </div>
            </div>
            <div className="student-details-col">
              <div className="student-detail-item">
                <span className="student-detail-label">Department</span>
                <span className="student-detail-value">
                  {selectedStudent.department}
                </span>
              </div>
              <div className="student-detail-item">
                <span className="student-detail-label">Batch</span>
                <span className="student-detail-value">
                  {selectedStudent.batch}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bill Form */}
      <form onSubmit={handleSubmit}>
        <div className="bill-form-section">
          <h2>Bill Details</h2>
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label className="form-label">Bill Title</label>
                <input
                  type="text"
                  name="title"
                  className="form-input disabled"
                  value={billDetails.title}
                  disabled
                  readOnly
                />
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label className="form-label">Amount (₹)</label>
                <input
                  type="number"
                  name="amount"
                  className="form-input"
                  placeholder="Enter amount"
                  value={billDetails.amount}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Remarks (Optional)</label>
            <textarea
              name="remarks"
              className="form-textarea"
              placeholder="Add any additional information about this bill"
              value={billDetails.remarks}
              onChange={handleInputChange}
            ></textarea>
          </div>
        </div>

        <div className="payment-methods-section">
          <h2>Payment Method</h2>
          <div className="form-group">
            <select
              className="form-select"
              value={paymentMethod}
              onChange={handlePaymentMethodChange}
              required
            >
              <option value="">Select payment method</option>
              {paymentMethods.map((method) => (
                <option key={method.type} value={method.type}>
                  {method.displayName || method.type}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Details - Updated to use the selected payment method details */}
          {paymentMethod && selectedPaymentMethodDetails && (
            <div className="payment-details">
              {(paymentMethod === "gpay" ||
                paymentMethod === "phonepe" ||
                paymentMethod === "upi") && (
                <div className="payment-details-row">
                  <div className="payment-details-col">
                    <div className="upi-container">
                      <div className="qr-code-container">
                        {/* Use actual QR code if available */}
                        {selectedPaymentMethodDetails.qrCode ? (
                          <img
                            src={getQrCodeUrl(
                              selectedPaymentMethodDetails.qrCode
                            )}
                            alt="UPI QR Code"
                            className="qr-code-image"
                          />
                        ) : (
                          <img
                            src="/api/placeholder/180/180"
                            alt="UPI QR Code"
                            className="qr-code-image"
                          />
                        )}
                      </div>
                      <div className="upi-id">
                        {selectedPaymentMethodDetails.upiId ||
                          "UPI ID not available"}
                      </div>
                      <button
                        type="button"
                        className="copy-button"
                        onClick={handleCopyUpiId}
                        disabled={!selectedPaymentMethodDetails.upiId}
                      >
                        {copied ? (
                          <>
                            <CheckCircle size={16} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy size={16} />
                            Copy UPI ID
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="payment-details-col">
                    <div className="form-group">
                      <label className="form-label">Instructions</label>
                      <ol
                        style={{
                          paddingLeft: "1.5rem",
                          fontSize: "0.9rem",
                          color: "#334155",
                        }}
                      >
                        <li>
                          Open your {selectedPaymentMethodDetails.displayName}{" "}
                          app
                        </li>
                        <li>Scan the QR code or enter the UPI ID</li>
                        <li>
                          Enter the exact amount: ₹{billDetails.amount || "0"}
                        </li>
                        <li>
                          Add student name and registration number in the note
                        </li>
                        <li>Complete the payment</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}

              {paymentMethod === "bank" && (
                <div className="payment-details-row">
                  <div className="payment-details-col">
                    <div className="bank-details">
                      <div className="bank-detail-item">
                        <span className="bank-detail-label">Account Name:</span>
                        <span className="bank-detail-value">
                          {selectedPaymentMethodDetails.accountName ||
                            "Not available"}
                        </span>
                      </div>
                      <div className="bank-detail-item">
                        <span className="bank-detail-label">
                          Account Number:
                        </span>
                        <span className="bank-detail-value">
                          {selectedPaymentMethodDetails.accountNumber ||
                            "Not available"}
                        </span>
                      </div>
                      <div className="bank-detail-item">
                        <span className="bank-detail-label">IFSC Code:</span>
                        <span className="bank-detail-value">
                          {selectedPaymentMethodDetails.ifscCode ||
                            "Not available"}
                        </span>
                      </div>
                      <div className="bank-detail-item">
                        <span className="bank-detail-label">Bank Name:</span>
                        <span className="bank-detail-value">
                          {selectedPaymentMethodDetails.bankName ||
                            "Not available"}
                        </span>
                      </div>
                      <div className="bank-detail-item">
                        <span className="bank-detail-label">Branch:</span>
                        <span className="bank-detail-value">
                          {selectedPaymentMethodDetails.branch ||
                            "Not available"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="payment-details-col">
                    <div className="form-group">
                      <label className="form-label">Instructions</label>
                      <ol
                        style={{
                          paddingLeft: "1.5rem",
                          fontSize: "0.9rem",
                          color: "#334155",
                        }}
                      >
                        <li>Make a transfer to the account details shown</li>
                        <li>
                          Use the student registration number as reference
                        </li>
                        <li>
                          Transfer the exact amount: ₹
                          {billDetails.amount || "0"}
                        </li>
                        <li>Upload payment receipt if available</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}

              {paymentMethod === "cash" && (
                <div className="payment-details-row">
                  <div className="payment-details-col">
                    <div className="bank-details">
                      <div className="bank-detail-item">
                        <span className="bank-detail-label">
                          Payment Method:
                        </span>
                        <span className="bank-detail-value">Cash Payment</span>
                      </div>
                      <div className="bank-detail-item">
                        <span className="bank-detail-label">
                          Amount to Pay:
                        </span>
                        <span className="bank-detail-value">
                          ₹{billDetails.amount || "0"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="payment-details-col">
                    <div className="form-group">
                      <label className="form-label">Instructions</label>
                      <ol
                        style={{
                          paddingLeft: "1.5rem",
                          fontSize: "0.9rem",
                          color: "#334155",
                        }}
                      >
                        <li>
                          Pay the exact amount in cash at the college accounts
                          office
                        </li>
                        <li>Collect a physical receipt from the cashier</li>
                        <li>Keep the receipt for your records</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="submit-section">
          <button
            type="submit"
            className="submit-button"
            disabled={!selectedStudent || !billDetails.amount || !paymentMethod}
          >
            Generate Bill
          </button>
        </div>
      </form>
      {showBillPreview && (
        <StudentBillPDF
          student={selectedStudent}
          billDetails={billDetails}
          paymentMethod={paymentMethod}
          paymentMethodDetails={selectedPaymentMethodDetails} // Pass the payment method details to the PDF component
          onClose={handleCloseBillPreview}
        />
      )}
    </div>
  );
}

export default CollegeBill;
